import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";

/**
 * Floating "Get a Free Valuation" CTA that fades in after the user scrolls.
 *
 * Props:
 *   - source: short slug appended as ?source=<slug> on the /contact link so the
 *     admin can see which page produced the lead.
 *   - ctaLabel / subtitle: optional overrides.
 *   - showAfter: scroll threshold in px (default 600).
 *
 * Dismissable per page-load (state only, no localStorage — re-appears next visit).
 */
export function StickyValuationCTA({
  source = "sticky",
  ctaLabel = "Get a Free Valuation",
  subtitle = "Quick chat, no obligation.",
  showAfter = 600,
}) {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const scrolled = window.scrollY > showAfter;
      // Hide near the page bottom so it doesn't overlap the footer CTA.
      const nearBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 300;
      setVisible(scrolled && !nearBottom);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [showAfter]);

  if (dismissed) return null;

  return (
    <div
      data-testid="sticky-valuation-cta"
      aria-hidden={!visible}
      className={`fixed z-40 transition-all duration-300 ease-out
        ${visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none"}
        bottom-4 right-4 left-4 sm:left-auto sm:bottom-6 sm:right-6`}
    >
      <div
        className="relative flex items-center gap-3 sm:gap-4 rounded-2xl bg-navy text-white shadow-2xl
          ring-1 ring-white/10 backdrop-blur-sm p-3 sm:p-4 sm:pr-5 max-w-md sm:max-w-none mx-auto"
      >
        {/* Gold accent bar */}
        <span className="hidden sm:block w-1 self-stretch rounded-full bg-gold flex-shrink-0" />

        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-sm sm:text-base leading-tight">
            Thinking of letting your property?
          </div>
          <div className="text-white/70 text-xs sm:text-sm mt-0.5 truncate">{subtitle}</div>
        </div>

        <Link
          to={`/contact?source=${encodeURIComponent(source)}`}
          data-testid="sticky-valuation-cta-button"
          className="inline-flex items-center gap-1.5 bg-gold hover:bg-gold/90 text-navy font-semibold text-sm
            px-4 py-2.5 rounded-full transition-colors whitespace-nowrap flex-shrink-0"
        >
          {ctaLabel}
          <ArrowRight className="w-4 h-4" />
        </Link>

        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          data-testid="sticky-valuation-cta-dismiss"
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white text-navy
            shadow-md hover:bg-slate-100 flex items-center justify-center transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default StickyValuationCTA;
